import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HPButton from "@components/common/Button";

interface PrimaryColorPickerProps {
  /** Label of whichever brand currently holds the Primary role. */
  primaryBrandLabel: string | null;
  /** Researcher's saved custom color, or null for the default theme blue. */
  color: string | null;
  /** Resolved default (the theme's current blue) — shown when `color` is null. */
  defaultColor: string;
  onChange: (color: string | null) => void;
}

/**
 * Primary Brand highlight color row — same pattern as a Bar Chart's
 * per-bar Custom color row (StylingSection in bar-chart-config): a native
 * color swatch with instant onChange, plus Apply/Cancel icons that appear
 * only while the edit is unconfirmed.
 */
const PrimaryColorPicker: React.FC<PrimaryColorPickerProps> = ({
  primaryBrandLabel,
  color,
  defaultColor,
  onChange,
}) => {
  const [pendingOriginal, setPendingOriginal] = useState<string | null | undefined>(undefined);
  const isDirty = pendingOriginal !== undefined;
  const currentColor = color ?? defaultColor;

  const handleCancel = () => {
    onChange(pendingOriginal ?? null);
    setPendingOriginal(undefined);
  };

  const handleResetToDefault = () => {
    onChange(null);
    setPendingOriginal(undefined);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Primary Brand highlight color
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "12px",
          }}
        >
          {primaryBrandLabel ?? "No Primary Brand selected"}
        </Typography>
        <input
          type="color"
          aria-label="Primary Brand highlight color"
          value={currentColor}
          disabled={!primaryBrandLabel}
          onChange={(e) => {
            if (pendingOriginal === undefined) setPendingOriginal(color);
            onChange(e.target.value);
          }}
          style={{
            width: 32,
            height: 24,
            cursor: "pointer",
            border: isDirty ? "2px solid #1976d2" : "1px solid #ccc",
            borderRadius: 4,
            padding: 0,
          }}
        />
        {isDirty && (
          <>
            <IconButton
              size="small"
              color="primary"
              title="Apply color"
              onClick={() => setPendingOriginal(undefined)}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" title="Cancel" onClick={handleCancel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
        {!isDirty && color !== null && (
          <HPButton size="small" variant="text" onClick={handleResetToDefault}>
            Reset to default
          </HPButton>
        )}
      </Box>
    </Box>
  );
};

export default PrimaryColorPicker;
